import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Phone,
  Clock,
  Send,
  AlertTriangle,
  Inbox,
  ExternalLink,
  Loader2,
} from "lucide-react";

const BUCKET_META = {
  fresh:         { label: "Frais (J0-J1)",       color: "bg-amber-50 text-amber-900 border-amber-200", icon: Inbox },
  to_remind:     { label: "À relancer (J2-J6)",  color: "bg-amber-100 text-amber-900 border-amber-300", icon: Clock },
  to_remind_again: { label: "2ᵉ relance (J7-J13)", color: "bg-orange-100 text-orange-900 border-orange-300", icon: AlertTriangle },
  abandoned:     { label: "Abandonné (J14+)",    color: "bg-rose-100 text-rose-900 border-rose-300", icon: AlertTriangle },
};

const FILTERS = [
  { key: "all", label: "Tous" },
  { key: "fresh", label: "Frais" },
  { key: "to_remind", label: "À relancer" },
  { key: "to_remind_again", label: "2ᵉ relance" },
  { key: "abandoned", label: "Abandonnés" },
];

const STATUS_LABEL = {
  sent: "Envoyée ✓",
  mocked: "MOCK (Twilio absent)",
  failed: "Échec",
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminDossiersPending() {
  const [data, setData] = useState({ items: [], buckets: {}, total: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/admin/dossiers/pending`);
      setData(r.data);
    } catch (e) {
      toast.error("Impossible de charger les dossiers en attente.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const items = filter === "all"
    ? data.items
    : data.items.filter((w) => w.bucket === filter);

  const remindOne = async (ws) => {
    setActingId(ws.id);
    try {
      const r = await apiClient.post(
        `/admin/dossiers/${ws.id}/remind-now`,
        {},
      );
      if (r.data.mocked) {
        toast.warning("Relance enregistrée en MOCK (Twilio non configuré en LIVE).");
      } else if (r.data.ok) {
        toast.success(`Relance niveau ${r.data.level} envoyée ✓`);
      } else {
        toast.error(`Envoi échoué : ${r.data.error || "erreur inconnue"}`);
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Relance impossible.");
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <header className="mb-8" data-testid="admin-dossiers-header">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Cockpit Admin</p>
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-900 mt-1">
            Dossiers en attente de complétion
          </h1>
          <p className="text-slate-600 mt-2 text-base">
            Suivi en temps réel des utilisateurs ayant reçu un lien mais n'ayant pas encore déposé de documents. Politique : max 2 relances automatiques, puis stop.
          </p>
        </header>

        {/* KPI buckets */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="bucket-kpis">
          {Object.entries(BUCKET_META).map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <Card key={key} className={`p-4 border ${meta.color}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider opacity-70">{meta.label}</div>
                    <div className="font-serif text-3xl mt-1">{data.buckets[key] || 0}</div>
                  </div>
                  <Icon className="w-6 h-6 opacity-50" />
                </div>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="dossier-filters">
          {FILTERS.map((f) => {
            const active = filter === f.key;
            const count = f.key === "all" ? data.total : (data.buckets[f.key] || 0);
            return (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 rounded-full text-sm border transition ${
                  active ? "bg-slate-900 text-white border-slate-900"
                         : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                }`}
              >
                {f.label} <span className={`ml-2 text-xs ${active ? "text-slate-300" : "text-slate-400"}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <Card className="p-10 text-center border-dashed">
            <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <h3 className="text-lg font-medium text-slate-800">Aucun dossier dans ce filtre</h3>
            <p className="text-sm text-slate-500 mt-1">
              Tous vos utilisateurs ont déposé leurs documents — ou aucun n'a atteint le délai de relance.
            </p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="dossier-list">
            {items.map((ws) => {
              const meta = BUCKET_META[ws.bucket] || BUCKET_META.fresh;
              const busy = actingId === ws.id;
              const reachedMax = (ws.reminders_sent || 0) >= 2;
              return (
                <Card key={ws.id} data-testid={`dossier-card-${ws.id}`} className="p-5 border-slate-200 hover:border-slate-300 transition">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <Badge className={`${meta.color} border`}>{meta.label}</Badge>
                        <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {ws.phone_masked || "—"}
                        </span>
                        {ws.origin_channel && (
                          <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                            {ws.origin_channel}
                          </span>
                        )}
                      </div>
                      <h3 className="font-medium text-slate-900">
                        {ws.first_name || <span className="text-slate-400">Sans prénom</span>}
                        <span className="text-xs text-slate-400 ml-2">{ws.id.slice(0, 8)}…</span>
                      </h3>
                      <div className="mt-2 grid sm:grid-cols-3 gap-2 text-xs text-slate-600">
                        <span>Créé : <strong>{fmt(ws.created_at)}</strong></span>
                        <span>Écoulé : <strong>{ws.elapsed_days}j</strong></span>
                        <span>Documents : <strong className={ws.doc_count > 0 ? "text-emerald-700" : "text-rose-700"}>{ws.doc_count}</strong></span>
                      </div>
                      {(ws.reminders_sent || 0) > 0 && (
                        <div className="mt-2 text-xs text-slate-500">
                          Relances : <strong>{ws.reminders_sent}</strong>/2
                          {ws.last_reminder_at && (
                            <> · dernière le {fmt(ws.last_reminder_at)} ({STATUS_LABEL[ws.last_reminder_status] || ws.last_reminder_status})</>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {ws.access_token && (
                        <a
                          href={`/d/${ws.access_token}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`open-workspace-${ws.id}`}
                          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
                        >
                          <ExternalLink className="w-4 h-4" /> Voir le dossier
                        </a>
                      )}
                      <Button
                        data-testid={`remind-btn-${ws.id}`}
                        disabled={busy || reachedMax}
                        onClick={() => remindOne(ws)}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                        title={reachedMax ? "Max de 2 relances déjà envoyées" : "Envoyer une relance maintenant"}
                      >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                        {reachedMax ? "Max atteint" : "Relancer"}
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
